import { Checkbox, cn, Label, RHF } from "@devhop/ui";
import { type ReactNode, useId } from "react";

type FormCheckboxCardProps = {
  name: string;
  label: ReactNode;
  description?: ReactNode;
  className?: string;
  disabled?: boolean;
};

function cardClassName(
  checked: boolean,
  disabled?: boolean,
  className?: string,
) {
  return cn(
    "flex min-h-11 w-full cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
    "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 hover:bg-muted/30",
    checked ? "border-primary bg-primary/5" : "border-border bg-background",
    disabled && "cursor-not-allowed opacity-50 hover:bg-background",
    className,
  );
}

/** Checkbox ขนาดกดง่าย — พื้นที่คลิกทั้งแถว (ใช้ภายใน FormRoot) */
export function FormCheckboxCard({
  name,
  label,
  description,
  className,
  disabled,
}: FormCheckboxCardProps) {
  const { Controller, useFormContext } = RHF;
  const { control } = useFormContext();
  const inputId = useId();

  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => {
        const checked = Boolean(field.value);
        return (
          <Label
            htmlFor={inputId}
            className={cardClassName(checked, disabled, className)}
          >
            <Checkbox
              id={inputId}
              checked={checked}
              onCheckedChange={(value) => field.onChange(value === true)}
              disabled={disabled}
              className="mt-0.5 size-5 shrink-0"
            />
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-sm leading-snug">
                {label}
              </span>
              {description ? (
                <span className="mt-1 block text-muted-foreground text-xs leading-relaxed">
                  {description}
                </span>
              ) : null}
            </span>
          </Label>
        );
      }}
    />
  );
}

type CheckboxCardProps = {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  className?: string;
  disabled?: boolean;
};

/** Checkbox ขนาดกดง่าย — ใช้นอกฟอร์ม (ไม่ผูก RHF) */
export function CheckboxCard({
  checked,
  onCheckedChange,
  label,
  description,
  className,
  disabled,
}: CheckboxCardProps) {
  const inputId = useId();

  return (
    <Label
      htmlFor={inputId}
      className={cardClassName(checked, disabled, className)}
    >
      <Checkbox
        id={inputId}
        checked={checked}
        onCheckedChange={
          onCheckedChange
            ? (value) => onCheckedChange(value === true)
            : undefined
        }
        disabled={disabled}
        className="mt-0.5 size-5 shrink-0"
      />
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-sm leading-snug">{label}</span>
        {description ? (
          <span className="mt-1 block text-muted-foreground text-xs leading-relaxed">
            {description}
          </span>
        ) : null}
      </span>
    </Label>
  );
}
